/**
 * The English source of truth. Every key here must exist in ar.ts — src/i18n.test.ts
 * fails the build otherwise, because a half-translated screen is worse than an
 * English one: the reader cannot tell whether a label is missing or the feature is.
 *
 * Client-facing surface only, on purpose. The Prismic-only screens (operations,
 * clients and people, portfolio, the activity log) stay English — they are used by
 * this team, not by the client, and translating them would be volume without a
 * reader. That gap is recorded in UX-BACKLOG.md rather than left to be discovered.
 */
export const en = {
  // navigation
  "nav.overview": "Overview",
  "nav.requests": "Requests",
  "nav.work": "The work",
  "nav.search": "Search",
  "nav.catalogue": "Service catalogue",
  "nav.catalogueEyebrow": "Agreed retainer rate card",
  "nav.statement": "Monthly statement",
  "nav.ledger": "Credit ledger",

  // shell
  "shell.available": "Available",
  "shell.requestService": "Request service",
  "shell.reportingMonth": "Reporting month",
  "shell.signOut": "Sign out",
  "shell.openNav": "Open navigation",
  "shell.closeNav": "Close navigation",
  "shell.theme": "Colour theme",
  "shell.themeLight": "Light theme",
  "shell.themeDark": "Dark theme",
  "shell.themeSystem": "System theme",
  "shell.language": "Language",

  // overview
  "overview.title": "Your production credit position",
  "overview.staffTitle": "Account control at a glance",
  "overview.intro": "Approved work is deducted against the agreed retainer rate card. Unused credits carry forward automatically.",
  "overview.summary": "Credit summary",
  "overview.availableCredits": "Available credits",
  "overview.readyToUse": "ready to use",
  "overview.creditsSpent": "Credits spent",
  "overview.completedWork": "completed work",
  "overview.creditsReserved": "Credits reserved",
  "overview.inProgressWork": "Approved and in-progress work",
  "overview.totalPool": "Total monthly pool",
  "overview.creditPosition": "Credit position",
  "overview.readyHeading": "{n} credits ready to use",
  "overview.monthlyAllocation": "Monthly allocation",
  "overview.carriedForward": "Carried forward",
  "overview.additionalCredits": "Additional credits",
  "overview.whereWent": "Where credits went",
  "overview.byCategory": "Spend by service category",
  "overview.latestActivity": "Latest activity",
  "overview.viewAll": "View all",

  // pace and forecast
  "pace.eyebrow": "Pace",
  "pace.title": "Are you on track?",
  "pace.thisVsLast": "This month against last",
  "pace.usualPace": "Usual pace",
  "pace.creditsPerMonth": "credits / month",
  "pace.noComparison": "No spend last month to compare against.",
  "pace.notEnoughHistory": "Not enough history",
  "pace.paceNeedsMonth": "A pace needs at least one completed month with work on it.",
  "pace.noForecast": "No forecast yet",
  "pace.likelyUnused": "likely unused",
  "pace.onPaceToUseAll": "On pace to use it all",
  "pace.buysExample": "{n} credits available buys, for example",
  "pace.estimateNotCommitment": "An estimate, not a commitment.",
  "pace.singleMonth": "Based on a single month so far — expect this to move.",

  // requests
  "requests.title": "Service requests",
  "requests.eyebrow": "Requests and delivery",
  "requests.workflow": "Production workflow",
  "requests.workflowSub": "Follow every request from brief to delivery",
  "requests.new": "New request",
  "requests.filters": "Request status filters",
  "requests.all": "All",
  "requests.colRequest": "Request",
  "requests.colRequestedBy": "Requested by",
  "requests.colSource": "Source",
  "requests.colCredits": "Credits",
  "requests.colDue": "Due",
  "requests.colStatus": "Status",
  "requests.colView": "View",
  "requests.details": "Request details",
  "requests.again": "Request this again",
  "requests.openDelivery": "Open the delivery",
  "requests.empty": "No requests yet",
  "requests.emptyHint": "Anything you ask Prismic for will appear here.",

  // statuses
  "status.pending": "Pending",
  "status.approved": "Approved",
  "status.in_progress": "In progress",
  "status.completed": "Completed",
  "status.rejected": "Rejected",
  "status.canceled": "Canceled",

  // the work
  "work.title": "The work",
  "work.eyebrow": "Everything Prismic delivered",
  "work.downloadAll": "Download everything",
  "work.emptyTitle": "Nothing delivered in {month}",
  "work.emptyHint": "Finished work appears here as soon as Prismic attaches it to a request.",

  // request form
  "form.chooseService": "Choose service",
  "form.addBrief": "Add brief",
  "form.whatToCreate": "What would you like us to create?",
  "form.chooseHint": "Choose a service to open its ready-to-send brief.",
  "form.searchServices": "Search {n} services",
  "form.allServices": "All services",
  "form.mostRequested": "Most requested",
  "form.mostRequestedSub": "The fastest way to start a common request",
  "form.moreServices": "More services",
  "form.browseAll": "Browse all {n} other services",
  "form.noServices": "No services found",
  "form.noServicesHint": "Try another keyword or choose a different category.",
  "form.changeService": "Change service",
  "form.essentials": "The essentials",
  "form.successLooksLike": "Tell us what success looks like",
  "form.quantity": "Quantity",
  "form.requestTitle": "Request title",
  "form.brief": "Brief",
  "form.briefPlaceholder": "Objective, key message, audience, formats, and any mandatory details",
  "form.dueDate": "Due date",
  "form.channel": "Channel",
  "form.channelPlaceholder": "Instagram, X, internal",
  "form.language": "Language",
  "form.referenceLink": "Reference link",
  "form.optional": "Optional",
  "form.referencePlaceholder": "Paste a mood board, folder, or example link",
  "form.estimated": "Estimated deduction",
  "form.included": "Included within working hours",
  "form.submit": "Submit request",
  "form.submitting": "Submitting…",
  "form.saveAsBrief": "Save as brief",
  "form.saving": "Saving…",
  "form.cancel": "Cancel",
  "form.newRequest": "New request",
  "form.sameAgain": "Same request again",
  "form.copiedTitle": "Copied from an earlier request",
  "form.fromTemplateTitle": "Started from a saved brief",
  "form.copiedHint": "The brief and quantity are filled in. Set a new date, and the credits shown are today's rate.",

  // saved briefs
  "briefs.eyebrow": "Saved briefs",
  "briefs.title": "Start from one you already wrote",
  "briefs.retired": "{service} is no longer offered",

  // search
  "search.title": "Search",
  "search.eyebrow": "Requests and ledger, whole contract",
  "search.placeholder": "Search requests and the ledger — a title, a brief, a service",
  "search.scope": "Everything on the account, across the whole contract.",
  "search.tooShort": "Type at least two characters.",
  "search.failed": "That search could not be run. Try again.",
  "search.searching": "Searching…",
  "search.nothing": "Nothing matched “{q}”",
  "search.nothingHint": "Try a shorter word, or part of a service name.",
  "search.requests": "Requests",
  "search.requestsSub": "Work you asked for",
  "search.ledger": "Ledger",
  "search.ledgerSub": "Credits in and out",
  "search.truncated": "Showing the 25 most recent. Narrow the search to see the rest.",

  // statement and ledger
  "statement.title": "Monthly statement",
  "statement.eyebrow": "Credits in, credits used, credits left",
  "statement.heading": "Statement of production credits",
  "statement.print": "Print",
  "statement.exportCsv": "Export CSV",
  "statement.downloadPdf": "Download PDF",
  "ledger.title": "Credit ledger",
  "ledger.eyebrow": "Monthly transaction history",
  "ledger.colService": "Service",
  "ledger.accountCredits": "Account credits",

  // shared
  "common.credits": "credits",
  "common.month": "Month",
  "common.notSet": "Not set",
  "common.loading": "Loading",
  "pace.deltaVs": "vs {month}",
  "pace.averagedOver": "Averaged over {n} completed month(s). This month is still running and is not counted.",
  "pace.atRateAcross": "At {rate}/month across {n} remaining month(s).",
  "pace.byMonth": "By {month}",
  "pace.monthsLeft": "{n} months left on the contract.",
  "pace.monthsLeft_one": "One month left on the contract.",
  "pace.nothingSpare": "At {rate}/month there is nothing spare. Plan extra work early.",
  "meter.spent": "spent",
  "meter.reserved": "reserved",
  "meter.available": "available",
  "meter.label": "{available} credits available from {total}",
  "overview.poolBreakdown": "{base} base + {added} added",
  "common.crEach": "{n} cr each",
  "common.cr": "cr",
  // service catalogue
  "catalogue.eyebrow": "One agreed rate card",
  "catalogue.title": "Choose any Prismic service",
  "catalogue.intro": "Monthly credits and any additional purchased credits use the same retainer rate card. Included services do not deduct credits.",
  "catalogue.monthly": "{n} credits a month",
  "catalogue.includedDeduct": "Included services deduct nothing",
  "catalogue.search": "Search services",
  "catalogue.category": "Service category",
  "catalogue.allCategories": "All categories",
  "catalogue.count": "{n} services",
  "catalogue.count_one": "One service",
  "catalogue.colService": "Service",
  "catalogue.colCategory": "Category",
  "catalogue.colUnit": "Unit",
  "catalogue.colCredits": "Credits",
  "catalogue.colAction": "Action",
  "catalogue.included": "Included",
  "catalogue.request": "Request",
  "catalogue.requestThis": "Request {service}",
  "catalogue.emptyTitle": "No matching services",
  "catalogue.emptyBody": "Try another keyword or category.",

  // the service picker on the request form — the catalogue being chosen from
  "picker.eyebrow": "One-click selection",
  "picker.title": "What would you like us to create?",
  "picker.intro": "Choose a service to open its ready-to-send brief.",
  "picker.search": "Search {n} services",
  "picker.search_one": "Search one service",
  "picker.searchLabel": "Search request services",
  "picker.categories": "Service categories",
  "picker.all": "All services",
  "picker.browseAll": "Browse all {n} other services",
  "picker.browseAll_one": "Browse the one other service",
  "picker.more": "More services",
  "picker.results": "Search results",
  "picker.featured": "Most requested",
  "picker.featuredSub": "The fastest way to start a common request",
  "picker.optionsAvailable": "{n} options available",
  "picker.optionsAvailable_one": "One option available",
  "picker.emptyTitle": "No services found",
  "picker.emptyBody": "Try another keyword or choose a different category.",
  // shared controls
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.print": "Print",
  "common.add": "Add",
  "common.none": "None",

  // the record of the relationship
  "nav.relationship": "The relationship",
  "nav.relationshipEyebrow": "Meetings, decisions, contacts and terms",
  "nav.review": "Quarterly review",
  "nav.reviewEyebrow": "The quarter, assembled",
  "relationship.eyebrow": "Everything behind the numbers",
  "relationship.title": "The record of this retainer",
  "relationship.intro": "Meetings held with you, the decisions that came out of them, who to talk to on both sides, and the paperwork behind the figures.",

  // renewal
  "renewal.title": "Where the contract stands",
  "renewal.term": "Contract term",
  "renewal.elapsed": "{n} months in",
  "renewal.elapsed_one": "One month in",
  "renewal.remaining": "{n} months left",
  "renewal.remaining_one": "One month left",
  "renewal.daysLeft": "{n} days left",
  "renewal.daysLeft_one": "One day left",
  "renewal.running": "On track",
  "renewal.conversationBy": "Open the renewal conversation by {date}",
  "renewal.conversationDue": "The renewal conversation is due now",
  "renewal.expiring": "The contract ends in {n} days",
  "renewal.expiring_one": "The contract ends tomorrow",
  "renewal.expired": "The contract ended on {date}",

  // contacts
  "contacts.title": "Key contacts",
  "contacts.intro": "Who approves on their side, and who owns it on ours.",
  "contacts.theirSide": "Their side",
  "contacts.ourSide": "Prismic",
  "contacts.approver": "Approves work",
  "contacts.add": "Add a contact",
  "contacts.emptyTitle": "No contacts recorded",
  "contacts.emptyBody": "Prismic adds the people who approve and own this account.",

  // meetings
  "meetings.title": "Meeting records",
  "meetings.intro": "Minutes of meetings held with you — not internal notes.",
  "meetings.add": "Record a meeting",
  "meetings.attendees": "Who was there",
  "meetings.summary": "What was agreed",
  "meetings.heldOn": "Held on",
  "meetings.emptyTitle": "No meetings recorded yet",
  "meetings.emptyBody": "Meetings held with you appear here, newest first.",
  "meetings.recordedBy": "Recorded by {name}",

  // decisions
  "decisions.title": "Decisions",
  "decisions.intro": "Why credits were spent, beside the ledger that says how many.",
  "decisions.add": "Record a decision",
  "decisions.decidedOn": "Decided on",
  "decisions.rationale": "Why",
  "decisions.aboutRequest": "About",
  "decisions.fromMeeting": "From",
  "decisions.noLink": "Not linked to a request",
  "decisions.emptyTitle": "No decisions recorded yet",
  "decisions.emptyBody": "The reasoning behind the spend appears here.",

  // contract documents
  "documents.title": "Contract documents",
  "documents.intro": "The signed retainer and the rate card behind the numbers.",
  "documents.add": "Add a document",
  "documents.emptyTitle": "No documents yet",
  "documents.emptyBody": "The signed retainer and rate card are filed here.",
  "documents.addedBy": "Added by {name}",

  // quarterly review
  "review.eyebrow": "The quarter, assembled",
  "review.title": "Quarterly review",
  "review.intro": "The three months, the work delivered, and the conversations that shaped it — assembled from the ledger, not restated.",
  "review.quarter": "Quarter",
  "review.granted": "Credits granted",
  "review.spent": "Credits spent",
  "review.committed": "Credits committed",
  "review.closing": "Credits left at quarter end",
  "review.byMonth": "Month by month",
  "review.delivered": "What was delivered",
  "review.deliveredCount": "{n} pieces of work",
  "review.deliveredCount_one": "One piece of work",
  "review.byCategory": "Where the credits went",
  "review.stillOpen": "Still open",
  "review.stillOpenBody": "Raised in this quarter and not yet delivered.",
  "review.nothingDelivered": "Nothing was delivered in this quarter",
  "review.partialQuarter": "This quarter is only partly inside the contract. {months} fall outside it and are not counted.",
  "review.outsideContract": "This quarter falls entirely outside the contract.",
  "review.noSpendCategories": "No completed spend in this quarter yet",
  "review.carriedIn": "Carried into the quarter",
  "review.grantedNote": "Monthly allocation and top-ups. Credits carried in from the previous quarter are counted once, on the left.",
  "review.unreconciled": "These figures do not add up to the ledger's closing balance of {closing} — a difference of {drift}. The closing figure above is the ledger's own; the parts are shown so you can see where the gap is.",
  "review.monthPool": "Pool that month",
  "review.monthPoolNote": "A month's pool is its allocation plus whatever carried into it, which is why the months do not sum to the quarter's grant.",
  "meetings.fieldTitle": "What the meeting was about",
  "meetings.edit": "Edit the meeting record",
  "decisions.fieldTitle": "What was decided",
  "decisions.edit": "Edit the decision",
  "contacts.edit": "Edit the contact",
  "contacts.fieldSide": "Which side",
  "contacts.fieldName": "Name",
  "contacts.fieldRole": "Role",
  "contacts.fieldEmail": "Email",
  "contacts.fieldPhone": "Phone",
  "contacts.fieldNote": "Note",
  "contacts.notApprover": "Not an approver",
  "close.title": "Closing this month",
  "close.intro": "Closing freezes what carries into next month. Nothing more can be billed to a closed month.",
  "close.willCarry": "Carries into next month",
  "close.pool": "Pool this month",
  "close.spent": "Spent",
  "close.committed": "Still committed",
  "close.action": "Close {month}",
  "close.reopen": "Reopen {month}",
  "close.closedBadge": "Closed",
  "close.closedOn": "Closed {date} by {name}",
  "close.carried": "{n} credits carried into next month, approved at close.",
  "close.provisional": "This figure moves until the month is closed.",
  "close.movingWork": "Closing moves this work to {to}. It has not been delivered, so it cannot stay billed to a closed month. Its price does not change:",
  "close.blockedNowhere": "This is the last month of the contract and work is still billed to it. There is no next month to move it to — deliver or cancel it first.",
  "close.blockedNextClosed": "{month} is already closed, so the outstanding work has nowhere to go. Reopen that month first.",
  "close.confirmMoving": "It also moves {n} outstanding request(s) to {to}.",
  "close.reopenSuperadmin": "Only a super admin can reopen a closed month.",
  "close.blockedPrevious": "Close the previous month first. Closing out of order freezes a figure that is still moving underneath it.",
  "close.reopenBlocked": "Cannot reopen — {month} is already closed. Reopen that one first.",
  "close.noteLabel": "Note for the record",
  "close.confirm": "Confirm and close",
  "close.confirmBody": "This carries {n} credits into next month and stops anything further being billed to {month}. A manager can reopen it.",
  "work.finalVersion": "Final",
  "work.filesInVersion": "{n} files",
  "work.filesInVersion_one": "One file",
  "share.title": "Share this work",
  "share.intro": "A read-only link for people outside your team. No credits, no ledger — just the work.",
  "share.create": "Create a link",
  "share.created": "Link created",
  "share.revoked": "Link revoked",
  "share.revoke": "Revoke",
  "share.copy": "Copy",
  "share.copied": "Copied",
  "share.copyFailed": "Could not copy — select the link and copy it manually.",
  "share.expires": "Stops working {date}",
  "share.opened": "Opened {n} times",
  "share.opened_one": "Opened once",
  "share.notOpened": "Not opened yet",
  "monthReview.eyebrow": "Your month, assembled",
  "monthReview.title": "{month} in review",
  "monthReview.intro": "Everything we made for you this month, what it used, and what carries forward.",
  "monthReview.running": "{month} is still running. These figures will move until it ends.",
  "monthReview.closedNote": "This month is closed. The figures below are final.",
  "monthReview.delivered": "Pieces delivered",
  "monthReview.used": "Credits used",
  "monthReview.carried": "Carrying forward",
  "monthReview.theWork": "What we made",
  "monthReview.nothingYet": "Nothing has been delivered this month yet",
  "monthReview.nothingYetBody": "Work appears here as soon as it is delivered.",
  "monthReview.effort": "Where the effort went",
  "monthReview.talked": "What we talked about",
  "monthReview.nothingTalked": "No meetings or decisions recorded this month",
  "monthReview.running_work": "Carrying into next month",
  "monthReview.runningBody": "Raised this month and not yet delivered.",
  "monthReview.seeStatement": "See the full statement",
  "monthReview.outside": "This month is outside the contract period.",
  "nav.monthReview": "Month in review",
  "nav.monthReviewEyebrow": "Your month, assembled",
  // Table headers. Defined ONCE because the same columns appear on the overview,
  // requests, the statement and the ledger — four screens that must not drift
  // into calling the same column different things.
  "table.request": "Request",
  "table.source": "Source",
  "table.credits": "Credits",
  "table.due": "Due",
  "table.status": "Status",
  "table.service": "Service",
  "table.date": "Date",
  "table.work": "Work",
  "table.value": "Value",
  "table.category": "Category",
  "table.share": "Share",
  "table.movement": "Movement",
  "table.transaction": "Transaction",
  "table.recordedBy": "Recorded by",
  "table.requestedBy": "Requested by",
  "table.approvedBy": "Approved by",
  "table.requested": "Requested",
  "table.quantity": "Quantity",
  "table.language": "Language",
  "table.view": "View",
  "table.action": "Action",

  // How a request reached us.
  "source.portal": "Portal",
  "source.whatsapp": "WhatsApp",
  "source.email": "Email",
  "source.meeting": "Meeting",
  "source.phone": "Phone",
  "source.workshop": "Workshop",
  "source.other": "Other",

  // overview — the rest of it
  "overview.requestsFor": "Requests for {month}",
  "overview.noSpendTitle": "No completed deductions yet",
  "overview.noSpendBody": "Completed work will appear here by category.",
  "overview.noRequestsTitle": "No requests in this month",
  "overview.noRequestsBody": "New portal requests and offline deliverables will be listed here.",
  "overview.createFirst": "Create first request",
  // requests — the list and the detail
  "requests.eyebrow2": "Production workflow",
  "requests.statusFilters": "Request status filters",
  "requests.viewRequest": "View request",
  "requests.emptyTitle": "No requests in this view",
  "requests.emptyBody": "Change the status filter or create a new request.",
  "requests.createRequest": "Create request",
  "requests.preferredDue": "Preferred due date",
  "requests.promisedBy": "Promised by Prismic",
  "requests.submittedBy": "Submitted by",
  "requests.actionedBy": "Actioned by",
  "requests.created": "Created",
  "requests.openReference": "Open reference",
  "requests.history": "History",
  "requests.timelineFailed": "Could not load the history for this request.",
  "requests.timelineEmpty": "No history recorded yet.",

  // the Prismic-side review panel on a request. Staff use it, but it sits inside a
  // screen the client also opens, so it is translated with the rest.
  "requestReview.title": "Prismic review",
  "requestReview.intro": "Approval reserves the final credit amount. Completion posts it to the ledger.",
  "requests.openDeliveryFor": "Open the delivery for {title}",
  "review.newStatus": "New status",
  "review.finalCredits": "Final credits",
  "review.promisedToClient": "Promised to the client",
  "review.productionStage": "Production stage",
  "review.note": "Optional clarification",


  // ledger
  "ledger.filters": "Ledger filters",
  "ledger.history": "Credit transaction history",
  "ledger.emptyTitle": "No ledger entries match this filter",
  "ledger.emptyBody": "Choose another transaction type.",
  // shell and shared chrome
  "shell.productionCredits": "Production credits",
  "shell.updating": "Updating view",
  "shell.mainNav": "Main navigation",
  "common.close": "Close",
  "work.title2": "The work",

  // the brief form
  "form.essentialsIntro": "Tell us what success looks like",
  "form.quantityLabel": "Request quantity",
  "form.decrease": "Decrease quantity",
  "form.increase": "Increase quantity",
  "form.dueDateLabel": "Preferred due date",
  "form.channelLabel": "Platform or channel",
  "form.arabic": "Arabic",
  "form.english": "English",

  // saved briefs
  "briefs.intro": "Start from one you already wrote",

  // the delivery hand-off page
  "delivery.unavailable": "This delivery is not available",
  "delivery.backToWork": "Back to the work",
  "delivery.allWork": "All delivered work",
  "delivery.whatThisWas": "What this was",
  "delivery.theBrief": "The brief",
  "delivery.howDidWeDo": "How did we do?",
  "delivery.alsoFromTeam": "Also from your team",
  "briefs.intro2": "Start from one you already wrote",
  "statement.retainer": "Prismic Media retainer",
  "statement.unreconciled": "This statement does not reconcile with the account balance.",
  "statement.summary": "Summary",
  "statement.availableThisMonth": "Credits available this month",
  "statement.billedThisMonth": "Delivered and billed this month",
  "statement.heldNote": "Held against the balance until the work completes",
  "statement.carryingForward": "Carrying forward to next month",
  "statement.detail": "Detail",
  "statement.workBilled": "Work billed this month",
  "statement.inFlight": "In flight",
  "statement.breakdown": "Breakdown",
  "statement.whereWent": "Where the credits went",
  "form.stepOf": "Step {n} of 2",
  "login.portalLabel": "Production credit portal",
  "login.heroAlt": "Prismic production studio display",
  "login.tagline1": "Every request.",
  "login.tagline2": "Every credit.",
  "login.tagline3": "One clear view.",
  "login.secureAccess": "Secure account access",
  "login.signInTitle": "Sign in to the dashboard",
  "login.email": "Email",
  "login.password": "Password",
  "login.passwordPlaceholder": "Enter your password",
  "login.localPreview": "Local preview",
  "login.privateWorkspace": "Private account workspace",
  "password.title": "Secure your account",
  "password.eyebrow": "Choose your own password",
  "password.current": "Password you signed in with",
  "password.new": "New password",
  "password.repeat": "Repeat new password",
  "password.mustMatch": "Both entries must match",
  "password.saving": "Saving",
  "password.save": "Save and continue",
  "password.signOutInstead": "Sign out instead",
  "consent.legend": "Email updates",
  "consent.body": "We can email you when a request is received, changes status, or is completed. Nothing else — no marketing. This is the only time we ask.",
  "consent.yes": "Yes, email me about my requests",
  "consent.no": "No, I will check the portal myself",
  "form.langBoth": "Arabic & English",
  "delivery.loading": "Loading the delivery",
  "catalogue.useStandard": "Use standard",
  "catalogue.changeRate": "Change",
  "catalogue.setRate": "Set rate",
  "share.gone": "This link is no longer available",
  "share.goneBody": "Shared links expire. Ask the person who sent it for a new one.",
  "share.open": "Open",
  "share.stopsWorking": "This link stops working on {date}.",
  "status.all": "All",
  "requests.staffHeading": "Review and account for every request",
  "requests.staffSub": "Approvals reserve credits; completion posts the final deduction to the ledger.",
  "requests.clientSub": "Estimated credits are confirmed by Prismic during approval, before production begins.",
  "error.loadDashboard": "Could not load the dashboard.",
  "workspace.opensTitle": "Your workspace opens in {month}",
  "workspace.opensBody": "Your contract starts then. Your credits appear on the first day.",
  "workspace.failedTitle": "We could not load your workspace.",
  "workspace.failedBody": "The problem is on our side. Try again in a moment.",
  "workspace.retry": "Try again",
  "error.signIn": "Could not sign in.",
  "error.openPreview": "Could not open preview.",
  "error.switchAccount": "Could not switch account.",
  "error.unreachable": "The dashboard could not be reached.",
  "toast.switchedTo": "Now working in {name}",
  "toast.passwordUpdated": "Password updated",
  "delivery.headerTitle": "Delivered work",
  "delivery.headerEyebrow": "Hand-off",
  "work.videoDelivered": "Video delivered",
  "work.documentDelivered": "Document delivered",
  "work.fromClient": "From the client",
  "work.deliveredByPrismic": "Delivered by Prismic",
  "work.attachWhenReady": "Attach the finished work when it is ready",
  "work.prismicWillAdd": "Prismic will add the finished work here",
  "error.loadFiles": "Could not load files.",
  "error.upload": "Could not upload.",
  "error.removeFile": "Could not remove the file.",
  "error.saveTemplate": "Could not save the template.",
  "error.submitRequest": "Could not submit the request.",
  "error.readMonth": "Could not read the month.",
  "error.closeMonth": "Could not close the month.",
  "error.reopenMonth": "Could not reopen the month.",
  "toast.fileAdded": "File added",
  "form.includedService": "Included service",
  "form.rateEstimate": "Rate-card estimate",
  "form.noDeduction": "No credit deduction",
  "form.estimatedDeduction": "Estimated deduction",
  "form.includedInHours": "Included within working hours",
  "form.submitRequest": "Submit request",
  "due.promised": "Promised",

  "form.savingBrief": "Saving…",
  "error.generic": "Something went wrong.",
  "error.deleteBrief": "Could not delete that brief.",
  "error.createLink": "Could not create the link.",
  "error.revokeLink": "Could not revoke the link.",
  "error.saveRate": "Could not save the rate.",
  "error.changePassword": "Could not change the password.",
  "error.loadDelivery": "Could not load this delivery.",
  "error.saveFeedback": "Could not save that.",
  "error.loadWork": "Could not load the work.",
  "error.buildReview": "Could not build the review.",
  "error.copyRequest": "That request could not be copied.",
  "error.loadRecord": "Could not load the record.",
  "error.removeContact": "Could not remove the contact.",
  "error.removeMeeting": "Could not remove the record.",
  "error.removeDecision": "Could not remove the decision.",
  "error.uploadDocument": "Could not upload the document.",
  "error.removeDocument": "Could not remove the document.",
  "error.saveMeeting": "Could not save the record.",
  "error.saveDecision": "Could not save the decision.",
  "error.saveContact": "Could not save the contact.",
  "error.updateRequest": "Could not update the request.",
  "toast.meetingUpdated": "Meeting record updated",
  "toast.meetingRecorded": "Meeting recorded",
  "toast.decisionUpdated": "Decision updated",
  "toast.decisionRecorded": "Decision recorded",
  "timeline.request_created": "Request submitted",
  "timeline.request_approved": "Approved by Prismic",
  "timeline.request_in_progress": "Production started",
  "timeline.request_completed": "Delivered",
  "timeline.request_rejected": "Declined",
  "timeline.request_canceled": "Canceled",
  "timeline.request_pending": "Sent back for review",
  "timeline.work_delivered": "Files uploaded",
  "timeline.brief_attached": "Brief attached",
  "timeline.attachment_removed": "File removed",
  "timeline.offline_delivery_logged": "Logged by Prismic",
  "timeline.feedback_given": "Feedback from the client",
  "timeline.stage_changed": "Stage moved",
  "timeline.promised_date_set": "Delivery date promised",
  "timeline.billing_month_moved": "Moved to the next month at close",
  "timeline.work_shared": "Shared with a read-only link",
  "timeline.share_revoked": "Share link revoked",
  "timeline.updated": "Updated",
  "stage.briefing": "Briefing",
  "stage.scripting": "Scripting",
  "stage.production": "Production",
  "stage.editing": "Editing",
  "stage.review": "Review",
  "stage.delivered": "Delivered",
  "kind.allocation": "Monthly allocation",
  "kind.rollover": "Carried forward",
  "kind.topup": "Additional credits",
  "kind.spend": "Service deduction",
  "kind.adjustment": "Adjustment",
  "kind.refund": "Credit refund",
  "kind.all": "All activity",
  "feedback.loved": "Love it",
  "feedback.good": "Happy with it",
  "feedback.changes": "Needs changes",
  "feedback.whatChanging": "What needs changing?",
  "feedback.whatWorked": "What worked?",
  "feedback.update": "Update",
  "feedback.someone": "Someone",
  "delivery.creditsUsed": "Credits used",
  "delivery.estimated": "Estimated",
  "login.signIn": "Sign in",
  "requests.notCommitted": "Not committed yet",
  "requests.deducted": "Deducted",
  "requests.managerApproval": "Manager approval required",
  "requests.updateRequest": "Update request",
  "form.startedFromBrief": "Started from a saved brief",
  "form.copiedFromRequest": "Copied from an earlier request",
  "gallery.inProgress": "In progress",
  "stage.aria": "Production stage: {stage}",
  "form.prefillNote": "The brief and quantity are filled in. Set a new date, and the credits shown are today's rate.",
  "col.movement": "Movement",
  "col.credits": "Credits",
  "col.value": "Value",
  "col.date": "Date",
  "col.approvedBy": "Approved by",
  "col.requested": "Requested",
  "col.status": "Status",
  "col.due": "Due",
  "col.category": "Category",
  "col.share": "Share",
  "col.request": "Request",
  "requests.estimatedDeduction": "Estimated deduction",
  "common.send": "Send",
  "common.saving": "Saving…",
  "source.admin": "Prismic",
  "source.system": "System",
  "common.exportCsv": "Export CSV",
  "deliverables.addToBrief": "Add to brief",
  "login.clientView": "Client view",
  "login.prismicView": "Prismic view",
  "requests.requestAgain": "Request this again",
  "ledger.note.allocation": "Contracted monthly production credits",
  "ledger.note.rollover": "Unused credits carried in from last month",
  "ledger.systemActor": "System",
  "ledger.entryCount": "{n} entries",
  "ledger.entryCount_one": "One entry",
  "requests.platformChannel": "Platform / channel",
  "requests.notSpecified": "Not specified",
  "requests.notActioned": "Not yet actioned",
  "work.nothingDelivered": "Nothing delivered yet",
  "delivery.inProduction": "In production — this page will fill in as the work lands.",
  "login.intro": "Review balances, request production services, and follow every approved deduction.",
  "form.nothingSubmitted": "Start the request below instead — nothing has been submitted.",
  "review.noteLabel": "Internal / ledger note",
  "statement.nothingDeducted": "Work is approved but has not been delivered yet, so nothing has been deducted.",
  "statement.approvedNotDelivered": "Approved, not yet delivered",
  "role.manager": "Prismic manager",
  "role.account_manager": "Prismic account manager",
  "role.admin": "Prismic admin",
  "role.superadmin": "Prismic super admin",
  "role.client": "Client",
  "ledger.completedWork": "Completed work",
  "statement.inflow.rollover": "Carried forward from last month",
  "statement.inflow.allocation": "Monthly contracted allocation",
  "statement.inflow.topup": "Additional credits purchased",
  "statement.inflow.refund": "Credits refunded",
  "statement.inflow.adjustment": "Manual adjustment",
  "statement.itemCount": "{n} items",
  "statement.itemCount_one": "One item",
  "statement.creditsHeld": "{n} credits held",
  "statement.footnote": "Credits are converted at {rate} KD each, as agreed in the retainer running {start} to {end}. Unused credits carry forward. Approved work is held against the balance and is only deducted once delivered.",
  "statement.driftBody": "Statement closing balance is {statementBalance} credits but the account reports {accountBalance} — a difference of {drift}. Treat the account balance as authoritative and tell Prismic before using this statement.",
  "statement.nothingBilled": "Nothing was billed in {month}",
  "statement.fullBalanceCarries": "No credits have been deducted this month. The full balance carries forward.",
  "shell.switchClient": "Working in {name}. Switch client.",
  "shell.chooseClient": "Choose a client",
  "shell.findClient": "Find a client",
  "shell.noClientMatch": "No client by that name",
  "overview.staffIntro": "{n} requests waiting for review. Offline work can be recorded from Prismic operations.",
  "overview.staffIntro_one": "One request waiting for review. Offline work can be recorded from Prismic operations.",
  "aria.removeFile": "Remove {name}",
  "aria.deleteBrief": "Delete the saved brief {name}",
  "aria.creditsFor": "Credits for {name}",
  "aria.openWork": "Open {name}",
  "aria.viewRequest": "View {name}",
  "aria.poolTotal": "{credits} credits in this month's pool",
  "toast.monthClosed": "{month} closed — {credits} credits carried forward",
  "toast.requestSubmitted": "Request submitted for review ({credits} credits).",
  "toast.requestMoved": "Request moved to {status}.",
  "toast.rateReset": "{name} back on the standard rate",
  "toast.rateSet": "{name} set to {credits} credits",
  "form.titlePlaceholder": "e.g. {service} for National Day",
  "briefs.confirmDelete": "Delete the saved brief \"{name}\"? Requests already raised from it are not affected.",
  "password.charsNeeded": "{n} more characters needed",
  "password.charsNeeded_one": "One more character needed",
  "password.minLength": "At least 12 characters",
} as const;
